import { useFtpeMasque } from '../FtpeMasqueToggle';
/**
 * DOMIUM Subscription Simulator - Workflow complet
 * COPPAM - Garanties Solidaires
 * Refactored: logique extraite dans hooks/ 
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { User, Building2, Calculator, FileText, Shield, RefreshCw, Info, HardHat, Coins, WifiOff, CloudOff, Cloud, Settings, AlertCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { getToken } from '../../services/tokenService';
import { syncAllPendingData, getLocalSimulations } from '../../services/offlineService';
import { API, ESS_COLORS, DOMIUM_SIMULATION_CREDIT_COST } from './constants';
import IdentiteTab from './IdentiteTab';
import OperationTab from './OperationTab';
import IntervenantsTab from './IntervenantsTab';
import TechniqueTab from './TechniqueTab';
import ResultatTab from './ResultatTab';
import ConfigPanel from './ConfigPanel';
import useFormState from './hooks/useFormState';
import useOfflineMode from './hooks/useOfflineMode';
import useSimulatorEngine from './hooks/useSimulatorEngine';
import useSignatureDrafts from './hooks/useSignatureDrafts';
import useDocumentActions from './hooks/useDocumentActions';
import useDomiumParametres from './hooks/useDomiumParametres';
import SimulationsSidebar from './SimulationsSidebar';
import useAdminConfig from './hooks/useAdminConfig';

export default function DOMIUMSubscriptionSimulator({ userRole = 'member', iaPrefill = null }) {
  const { user, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('identite');
  const [savedSimulation, setSavedSimulation] = useState(null);
  const [result, setResult] = useState(null);
  const [intervenantsDocuments, setIntervenantsDocuments] = useState({});
  const [simSearchQuery, setSimSearchQuery] = useState('');

  // --- Custom Hooks ---
  const {
    identite, setIdentite, operation, setOperation,
    technique, setTechnique, etudes, setEtudes,
    intervenants, setIntervenants,
    addIntervenant, removeIntervenant, updateIntervenant,
    isPersonneMorale, restoreDraftData,
  } = useFormState({ getAuthHeader });

  const {
    isOnline, unsyncedCount: unsyncedCount,
    localSimulations, setLocalSimulations, showLocalSimulations, setShowLocalSimulations,
    saveToLocal, handleDeleteLocalSimulation,
  } = useOfflineMode({ userId: user?.id });

  const {
    isExemptFromCredits, isSuperAdmin, headers,
    loading: loading, calculating, saving, config, setConfig,
    isFtpeMember: isFtpeMemberReel, creditBalance, creditLoading,
    simulations, fetchSimulations, handleLoadServerSimulation, handleSaveSimulation, handleDuplicateSimulation, handleDeleteSimulation,
  } = useSimulatorEngine({
    user, userRole, identite, operation, technique, etudes, intervenants,
    isOnline, saveToLocal, setSavedSimulation, setActiveTab, result, setResult,
    restoreDraftData,
  });

  const [ftpeMasque] = useFtpeMasque();
  const isFtpeMember = isFtpeMemberReel || (isSuperAdmin && ftpeMasque)
    || !!savedSimulation?.parametres_devis?.deja_adherent;
  const { onPlanChange } = useDomiumParametres({ isSuperAdmin, savedSimulation, setSavedSimulation, isFtpeMember, headers });

  const {
    showConfigPanel, setShowConfigPanel, adminConfig, savingConfig, configForm, setConfigForm,
    saveAdminConfig, resetAdminConfig,
  } = useAdminConfig({ isSuperAdmin, headers, setConfig });

  const {
    signingInProgress, otpSent, otpCode, setOtpCode,
    signatureMethod, signatureDestination,
    savingDraft,
    handleInitiateSignature, handleValidateSignature, handleResendOTP,
    handleSaveDraft,
  } = useSignatureDrafts({
    identite, operation, technique, headers, config, result,
    fetchSimulations, setSavedSimulation, setActiveTab, restoreDraftData,
  });

  const {
    downloadingDoc, confirmingVirement, confirmingFTPE,
    handleDownloadDocument, handlePrintDocumentsList,
    handleConfirmVirement, handleConfirmFTPEPayment,
  } = useDocumentActions({ headers, fetchSimulations, setSavedSimulation });

  // Auto-préremplissage depuis l'analyse IA des documents (onglet « Analyse IA »)
  useEffect(() => {
    if (iaPrefill && (iaPrefill.identite || iaPrefill.operation || iaPrefill.intervenants)) {
      restoreDraftData(iaPrefill);
      setActiveTab('identite');
      toast.success('Formulaire pré-rempli à partir de l\'analyse IA. Vérifiez les champs.');
    }
  }, [iaPrefill?._ts]);

  // Bridge: charger une simulation locale (croise plusieurs hooks)
  const handleLoadLocalSimulation = useCallback((simulation) => {
    if (simulation.identite) setIdentite(simulation.identite);
    if (simulation.operation) setOperation(simulation.operation);
    if (simulation.technique) setTechnique(simulation.technique);
    if (simulation.result) setResult(simulation.result);
    setIntervenantsDocuments(simulation.intervenants_documents || {});
    setShowLocalSimulations(false);
    toast.success('Simulation DOMIUM chargee depuis le stockage local');
  }, [setIdentite, setOperation, setTechnique, setResult, setShowLocalSimulations]);

  // Sync intervenants documents from saved simulation
  React.useEffect(() => {
    if (savedSimulation?.intervenants_documents) {
      setIntervenantsDocuments(savedSimulation.intervenants_documents);
    }
  }, [savedSimulation]);

  const handleDocUploaded = useCallback((intId, docType, docMeta) => {
    setIntervenantsDocuments(prev => {
      const updated = { ...prev };
      if (!updated[intId]) updated[intId] = {};
      if (docMeta) { updated[intId] = { ...updated[intId], [docType]: docMeta }; }
      else { const copy = { ...updated[intId] }; delete copy[docType]; updated[intId] = copy; }
      return updated;
    });
  }, []);

  // Auto-load simulation from URL param ?resume=REF
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resumeRef = params.get('resume');
    if (resumeRef && !savedSimulation) {
      handleLoadServerSimulation(resumeRef);
      window.history.replaceState({}, '', window.location.pathname);
    }
  // eslint-disable-next-line
  }, []);

  // Filtrage des simulations par nom/raison sociale/montant
  const filteredSimulations = useMemo(() => {
    if (!simSearchQuery.trim()) return simulations;
    const q = simSearchQuery.trim().toLowerCase();
    return simulations.filter(sim => {
      const nom = (sim.nom || '').toLowerCase();
      const ref = (sim.reference || '').toLowerCase();
      const montant = sim.contribution_ht != null ? sim.contribution_ht.toLocaleString('fr-FR') : '';
      return nom.includes(q) || ref.includes(q) || montant.includes(q);
    });
  }, [simulations, simSearchQuery]);

  return (
    <div className="space-y-6" data-testid="domium-subscription-simulator">
      {/* En-tete compact (aligne sur BATICO v20 — meme hauteur, badges fonces) */}
      <div
        className="relative rounded-xl p-4 text-white shadow-xl border border-amber-300/15 overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${ESS_COLORS.vertPrincipal} 0%, ${ESS_COLORS.bleuInstitutionnel} 100%)` }}
      >
        {/* Filet officiel doré */}
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white/85 mb-2">
          <span className="inline-block w-7 h-px bg-amber-300/80" />
          <span className="font-semibold">COPPAM</span>
          <span className="text-white/40">|</span>
          <span>Garanties Solidaires</span>
          <span className="text-white/40">|</span>
          <span>Dommages à l'Ouvrage</span>
        </div>

        <div className="flex items-center gap-4">
          {/* Sceau institutionnel */}
          <div className="relative hidden sm:block flex-shrink-0">
            <div className="absolute inset-0 bg-amber-300/20 rounded-xl blur-xl"></div>
            <div className="relative inline-flex items-center justify-center w-12 h-12 bg-white/12 backdrop-blur-md text-white rounded-xl shadow-lg border border-white/25">
              <Shield className="h-6 w-6 drop-shadow-lg" />
            </div>
          </div>

          {/* Titre institutionnel */}
          <div className="flex-1 min-w-0">
            <h1 className="text-[clamp(1.05rem,1.9vw,1.4rem)] leading-[1.1] font-black tracking-tight text-white drop-shadow-lg" style={{ fontFamily: '"Playfair Display", "Georgia", serif' }}>
              SIMULATEUR CADRE <span className="text-amber-200 font-bold">DOS-DOMIUM</span>
            </h1>
            <p className="text-[12px] md:text-[12.5px] text-white/90 mt-1 font-medium max-w-3xl">
              Dommages à l'ouvrage — Art. 1792 et suivants du Code civil.
            </p>
            <div className="mt-1 space-y-0.5 text-[10px] text-white/65 leading-snug max-w-3xl">
              <div>Cadre coopératif — Loi n° 47-1775 du 10 septembre 1947 portant statut de la coopération.</div>
              <div>Économie sociale et solidaire — Loi n° 2014-856 du 31 juillet 2014 relative à l'ESS.</div>
            </div>
          </div>

          {/* Badges institutionnels — foncés, lisibles (parité BATICO) */}
          <div className="hidden lg:flex flex-col gap-1.5 items-end flex-shrink-0">
            {isSuperAdmin && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowConfigPanel(!showConfigPanel)}
                className="bg-white/15 border-white/30 text-white hover:bg-white/25 h-7 px-2.5 text-[11px]"
              >
                <Settings className="h-3 w-3 mr-1" />
                Configuration
              </Button>
            )}
            {!isOnline && (
              <Badge className="text-[10px] px-2.5 py-1 font-semibold border-0 bg-amber-500 text-white shadow-md hover:bg-amber-600 animate-pulse">
                <WifiOff className="h-3 w-3 mr-1" />
                Mode hors ligne
              </Badge>
            )}
            {localSimulations.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowLocalSimulations(!showLocalSimulations)}
                className="bg-white/15 border-white/30 text-white hover:bg-white/25 h-7 px-2.5 text-[11px]"
              >
                <CloudOff className="h-3 w-3 mr-1" />
                {localSimulations.length} local
              </Button>
            )}
            <Badge className="text-[10px] px-2.5 py-1 font-semibold border-0 bg-emerald-700 text-white shadow-md hover:bg-emerald-800">
              <Shield className="h-3 w-3 mr-1" /> Garantie 10 ans
            </Badge>
            <div className="flex gap-1.5">
              <Badge className="text-[10px] px-2 py-0.5 font-semibold border-0 bg-blue-700 text-white shadow-sm hover:bg-blue-800">Étape A</Badge>
              <Badge className="text-[10px] px-2 py-0.5 font-semibold border-0 bg-indigo-700 text-white shadow-sm hover:bg-indigo-800">Solidaire</Badge>
            </div>
          </div>
        </div>

        {/* Filet inférieur fin doré */}
        <div className="mt-3 h-px bg-gradient-to-r from-transparent via-amber-300/60 to-transparent" />
      </div>
      
      {/* Panneau de configuration admin */}
      <ConfigPanel
        showConfigPanel={showConfigPanel} setShowConfigPanel={setShowConfigPanel}
        configForm={configForm} setConfigForm={setConfigForm}
        savingConfig={savingConfig}
        saveAdminConfig={saveAdminConfig}
        resetAdminConfig={resetAdminConfig}
        adminConfig={adminConfig}
      />

      {showLocalSimulations && localSimulations.length > 0 && (
        <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500 rounded-xl">
                  <CloudOff className="h-5 w-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-lg text-amber-900">Simulations DOMIUM locales</CardTitle>
                  <CardDescription className="text-amber-700">
                    {localSimulations.length} simulation(s) - Cliquez pour charger
                  </CardDescription>
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setShowLocalSimulations(false)} className="text-amber-700 hover:bg-amber-100">
                Fermer
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {localSimulations.map((sim) => (
                <div key={sim.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200 hover:border-amber-400 transition-colors">
                  <div className="flex-1 cursor-pointer" onClick={() => handleLoadLocalSimulation(sim)}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-800">
                        {sim.identite?.nom || sim.identite?.raison_sociale || 'Sans nom'}
                      </span>
                      {sim.result?.offline_estimation && (
                        <Badge className="bg-amber-600 text-white text-xs font-semibold">Estimation</Badge>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Cout: {parseFloat(sim.technique?.cout_construction_ht || 0).toLocaleString('fr-FR')} EUR -
                      {' '}{new Date(sim.created_at).toLocaleString('fr-FR')}
                      {!sim.synced && <span className="ml-2 text-amber-600">- Non synchronise</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={() => handleLoadLocalSimulation(sim)} className="text-blue-600 hover:bg-blue-50">Charger</Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteLocalSimulation(sim.id)} className="text-red-600 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {isOnline && localSimulations.some(s => !s.synced) && (
              <div className="mt-3 pt-3 border-t border-amber-200">
                <Button
                  size="sm"
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  onClick={async () => {
                    const token = getToken();
                    if (token) {
                      const r = await syncAllPendingData(API, token);
                      if (r?.success) {
                        toast.success('Synchronisation terminee');
                        const updated = await getLocalSimulations('domium');
                        setLocalSimulations(updated);
                      }
                    }
                  }}
                >
                  <Cloud className="h-4 w-4 mr-2" />
                  Synchroniser tout
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Bandeau mode hors ligne */}
      {!isOnline && (
        <Alert className="border-amber-300 bg-amber-50">
          <WifiOff className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Mode hors ligne active</strong> - Vos simulations seront sauvegardees localement 
            et synchronisees automatiquement a la reconnexion. Les calculs sont des estimations approximatives.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Mention juridique */}
      <Alert className="border-amber-200 bg-amber-50">
        <AlertCircle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          <strong>Mention juridique :</strong> Ce dispositif constitue un mecanisme solidaire de 
          mutualisation des risques de nature civile. <strong>Il ne constitue ni une assurance, 
          ni une garantie automatique au sens du Code des assurances.</strong>
        </AlertDescription>
      </Alert>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulaire principal */}
        <div className="lg:col-span-2">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-5 gap-2 h-auto p-1 mb-4">
              <TabsTrigger value="identite" className="py-3" data-testid="tab-identite">
                <User className="h-4 w-4 mr-2" />Identite
              </TabsTrigger>
              <TabsTrigger value="operation" className="py-3" data-testid="tab-operation">
                <Building2 className="h-4 w-4 mr-2" />Operation
              </TabsTrigger>
              <TabsTrigger value="intervenants" className="py-3" data-testid="tab-intervenants">
                <HardHat className="h-4 w-4 mr-2" />Intervenants
              </TabsTrigger>
              <TabsTrigger value="technique" className="py-3" data-testid="tab-technique">
                <Calculator className="h-4 w-4 mr-2" />Technique
              </TabsTrigger>
              <TabsTrigger value="resultat" className="py-3" data-testid="tab-resultat">
                <FileText className="h-4 w-4 mr-2" />Resultat
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="identite" className="space-y-6">
              <IdentiteTab identite={identite} setIdentite={setIdentite} operation={operation} isPersonneMorale={isPersonneMorale} setActiveTab={setActiveTab} />
            </TabsContent>

            <TabsContent value="operation" className="space-y-6">
              <OperationTab identite={identite} operation={operation} setOperation={setOperation} setActiveTab={setActiveTab} />
            </TabsContent>

            <TabsContent value="intervenants" className="space-y-6">
              <IntervenantsTab intervenants={intervenants} setIntervenants={setIntervenants} addIntervenant={addIntervenant} removeIntervenant={removeIntervenant} updateIntervenant={updateIntervenant} setActiveTab={setActiveTab} reference={savedSimulation?.reference} headers={headers} intervenantsDocuments={intervenantsDocuments} onDocUploaded={handleDocUploaded} onAutoSave={handleSaveSimulation} />
            </TabsContent>

            <TabsContent value="technique" className="space-y-6">
              <TechniqueTab technique={technique} setTechnique={setTechnique} intervenants={intervenants} etudes={etudes} setEtudes={setEtudes} savingDraft={savingDraft} handleSaveDraft={handleSaveDraft} saving={saving} handleSaveSimulation={handleSaveSimulation} setActiveTab={setActiveTab} />
            </TabsContent>

            <TabsContent value="resultat" className="space-y-6">
              <ResultatTab
                onPlanChange={onPlanChange}
                savedSimulation={savedSimulation}
                identite={identite}
                operation={operation}
                technique={technique}
                intervenants={intervenants}
                result={result}
                isFtpeMember={isFtpeMember}
                downloadingDoc={downloadingDoc}
                otpSent={otpSent}
                signingInProgress={signingInProgress}
                signatureMethod={signatureMethod}
                signatureDestination={signatureDestination}
                otpCode={otpCode}
                setOtpCode={setOtpCode}
                confirmingVirement={confirmingVirement}
                confirmingFTPE={confirmingFTPE}
                saving={saving}
                handleSaveSimulation={handleSaveSimulation}
                setActiveTab={setActiveTab}
                handleDownloadDocument={handleDownloadDocument}
                handlePrintDocumentsList={handlePrintDocumentsList}
                handleValidateSignature={handleValidateSignature}
                handleInitiateSignature={handleInitiateSignature}
                handleResendOTP={handleResendOTP}
                handleConfirmVirement={handleConfirmVirement}
                handleConfirmFTPEPayment={handleConfirmFTPEPayment}
              />
            </TabsContent>
          </Tabs>
        </div>
        
        {/* Panneau lateral - Calcul en temps reel */}
        <div className="space-y-6">
          {!isExemptFromCredits && (
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Coins className="h-5 w-5 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">Mes Credits</span>
                  </div>
                  <div className="text-right">
                    {creditLoading ? (
                      <RefreshCw className="h-4 w-4 animate-spin text-amber-600" />
                    ) : (
                      <>
                        <span className={`text-lg font-bold ${creditBalance !== null && creditBalance < DOMIUM_SIMULATION_CREDIT_COST ? 'text-red-600' : 'text-amber-700'}`}>
                          {creditBalance !== null ? creditBalance : '--'}
                        </span>
                        <span className="text-xs text-amber-600 ml-1">credits</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="mt-2 text-xs text-amber-700">
                  <span className="flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    Cout simulation : {DOMIUM_SIMULATION_CREDIT_COST} credits
                  </span>
                </div>
                {creditBalance !== null && creditBalance < DOMIUM_SIMULATION_CREDIT_COST && (
                  <Alert variant="destructive" className="mt-2 py-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-xs">Credits insuffisants pour sauvegarder une simulation</AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}
          
          <SimulationsSidebar
            calculating={calculating}
            result={result}
            savedSimulation={savedSimulation}
            config={config}
            identite={identite}
            isFtpeMember={isFtpeMember}
            simulations={simulations}
            filteredSimulations={filteredSimulations}
            simSearchQuery={simSearchQuery}
            setSimSearchQuery={setSimSearchQuery}
            handleLoadServerSimulation={handleLoadServerSimulation}
            handleDuplicateSimulation={handleDuplicateSimulation}
            handleDeleteSimulation={handleDeleteSimulation}
          />
        </div>
      </div>
    </div>
  );
}
